import { useState, useReducer, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import IconButton from '@mui/material/IconButton';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import ChatInterface from './ChatInterface';
import NotesInterface from './NotesInterface';
import ConversationList from './ConversationList';
import './AIMeAssistant.css'; // Keep this for now, but we'll gradually replace styles

// MVP AIMeAssistant with collapsible drawer
const AIMeAssistant = ({ open, setOpen, tenantId }) => {
	const theme = useTheme();
	const [mode, setMode] = useState('chat'); // chat, notes, or conversations
	const [currentMentions, setCurrentMentions] = useState([]);
	const [isTenantUser, setIsTenantUser] = useState(true); // Default to true until we check
	const [state, setState] = useReducer(
		(state, newState) => ({ ...state, ...newState }),
		{
			currentThreadId: null,
		}
	);

	// Check user role and permissions
	useEffect(() => {
		// Get user role from session storage
		let role = null;

		// Try to get role from user object first
		const userStr = sessionStorage.getItem('user');
		if (userStr) {
			try {
				const user = JSON.parse(userStr);
				role = user.role;
			} catch (e) {
				console.error('Error parsing user object:', e);
			}
		}

		// Fallback to direct role key
		if (!role) {
			role = sessionStorage.getItem('role');
		}

		// Check if user has an Altvia role (0, 1, 2) or a tenant role (3, 4, 5)
		const isAltviaNonTenantRole =
			role === 'altvia_admin' ||
			role === 'altvia_power_user' ||
			role === 'altvia_user';

		if (isAltviaNonTenantRole) {
			setIsTenantUser(false);
		} else {
			// User has tenant association, proceed with normal setup
			setIsTenantUser(true);
		}
	}, []);

	const toggleMode = (newMode) => {
		if (mode === newMode) {
			setOpen(false); // Close drawer if clicking the active tab
		} else {
			// When switching modes, preserve the current thread unless we're starting a new chat
			setMode(newMode);
		}
	};

	return (
		<Box
			className="aime-container"
			sx={{
				width: open ? '450px' : 0,
				minWidth: open ? '450px' : 0,
				height: 'calc(100vh - 64px)',
				bgcolor: theme.palette.background.default,
				display: 'flex',
				flexDirection: 'column',
				transition:
					'width 0.35s cubic-bezier(.4,0,.2,1), min-width 0.35s cubic-bezier(.4,0,.2,1)',
				boxShadow: theme.shadows[3],
				position: 'relative',
				flexShrink: 0,
				overflow: open ? 'visible' : 'hidden',
			}}
		>
			{open && (
				<Box
					className="aime-inner-container"
					sx={{
						maxWidth: 'none',
						width: '100%',
						height: '100%',
						margin: 0,
						border: 'none',
						overflow: 'hidden',
						display: 'flex',
						flexDirection: 'column',
						position: 'relative',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							padding: '12px 16px',
							bgcolor:
								theme.palette.mode === 'dark'
									? theme.palette.background.paper
									: theme.palette.primary.main,
							color: '#fff',
							position: 'sticky',
							top: 0,
							zIndex: 100,
							borderRadius: '0 0 8px 8px', // Only round the bottom corners
						}}
					>
						<Box sx={{ display: 'flex', alignItems: 'center' }}>
							<IconButton
								size='small'
								onClick={() => setOpen(false)}
								sx={{
									color: '#fff',
									mr: 1,
								}}
							>
								<KeyboardDoubleArrowRightIcon />
							</IconButton>
							<Typography
								variant='h6'
								sx={{ fontSize: '18px', fontWeight: 600, color: '#fff' }}
							>
								AIMe Assistant
							</Typography>
						</Box>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							<IconButton
								size='small'
								onClick={() => toggleMode('chat')}
								sx={{
									color: '#fff',
									bgcolor:
										mode === 'chat' ? 'rgba(255,255,255,0.2)' : 'transparent',
									'&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
								}}
								title='Chat Mode'
							>
								<ChatBubbleOutlineIcon fontSize='small' />
							</IconButton>
							<IconButton
								size='small'
								onClick={() => toggleMode('conversations')}
								sx={{
									color: '#fff',
									bgcolor:
										mode === 'conversations'
											? 'rgba(255,255,255,0.2)'
											: 'transparent',
									'&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
								}}
								title='Conversation List'
							>
								<FormatListBulletedIcon fontSize='small' />
							</IconButton>
							<IconButton
								size='small'
								onClick={() => toggleMode('notes')}
								sx={{
									color: '#fff',
									bgcolor:
										mode === 'notes' ? 'rgba(255,255,255,0.2)' : 'transparent',
									'&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
								}}
								title='Notes Mode'
							>
								<EditNoteIcon fontSize='small' />
							</IconButton>
						</Box>
					</Box>
					<Box
						className="aime-content-area"
						sx={{
							bgcolor: theme.palette.background.default,
							borderLeft: `1px solid ${theme.palette.divider}`,
							flex: '1 1 auto',
							display: 'flex',
							flexDirection: 'column',
							height: 'calc(100% - 56px)', // Subtract header height only
							overflow: 'auto',
							position: 'relative',
							paddingBottom: 0
						}}
					>
						{!isTenantUser ? (
							<Box
								sx={{
									width: '100%',
									height: '100%',
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									p: 3,
								}}
							>
								<Box
									sx={{
										maxWidth: 500,
										textAlign: 'center',
										p: 4,
										borderRadius: 2,
										bgcolor:
											theme.palette.mode === 'dark'
												? 'rgba(255,255,255,0.05)'
												: 'rgba(0,0,0,0.03)',
									}}
								>
									<Typography variant='h5' color='text.primary' gutterBottom>
										AIMe Unavailable
									</Typography>
									<Typography variant='body1' color='text.secondary' paragraph>
										AIMe is not available for system admin users without tenant
										association.
									</Typography>
									<Typography variant='body2' color='text.secondary'>
										Please log in with a tenant user account to use this functionality.
									</Typography>
								</Box>
							</Box>
						) : mode === 'chat' ? (
							<ChatInterface
								currentMentions={currentMentions}
								setCurrentMentions={setCurrentMentions}
								threadId={state.currentThreadId}
								setThreadId={(id) =>
									setState((prev) => ({ ...prev, currentThreadId: id }))
								}
								isTenantUser={isTenantUser}
							/>
						) : mode === 'notes' ? (
							<NotesInterface
								tenantId={tenantId}
							/>
						) : (
							<ConversationList
								onSelectThread={(threadId) => {
									// First set the thread ID, then change mode to ensure the ID is available when chat renders
									setState({ currentThreadId: threadId });
									setTimeout(() => setMode('chat'), 50);
								}}
								onNewChat={() => {
									setState({ currentThreadId: null });
									setMode('chat');
								}}
								selectedThreadId={state.currentThreadId}
								isTenantUser={isTenantUser}
							/>
						)}
					</Box>
				</Box>
			)}
		</Box>
	);
};

export default AIMeAssistant;
